/**
 * Helpers puros do formulário de novo caso (extraídos de NovoCasoPage em
 * E23.F3 p/ manter o componente enxuto e testável). Sem JSX, sem hooks.
 */
import { ptBR } from '@/i18n/pt-BR';
import {
  CaseError,
  MODALIDADE_DEFAULT,
  type CaseCreateData,
  type CaseRecordData,
  type CaseUpdateData,
  type Modalidade,
} from '@/lib/cases';

export const CNJ_REGEX = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;
export const CNJ_PLACEHOLDER = '—';
// E9.F2.T12: CPF aceita formato com ou sem máscara (apenas dígitos, pontos
// e hífen). Sem validação de dígito verificador — backend tem CHECK constraint
// equivalente; perito pode estar registrando um CPF informalmente passado.
export const CPF_REGEX = /^[0-9.-]+$/;

// E163.F1 (ADR-0112 §4): sem valor padrão — vazio é "não informado", e o caso grava nulo.
export const ESPECIME_OPTIONS = [
  { value: '', label: ptBR.casos.especimeNaoInformado },
  { value: 'Análise do CCB', label: ptBR.casos.especimeCcb },
  { value: 'Contrato', label: ptBR.casos.especimeContrato },
  { value: 'Cheque', label: ptBR.casos.especimeCheque },
  { value: 'Procuração', label: ptBR.casos.especimeProcuracao },
  { value: 'Testamento', label: ptBR.casos.especimeTestamento },
  { value: 'Aval em nota promissória', label: ptBR.casos.especimeAval },
  { value: 'Termo de quitação', label: ptBR.casos.especimeQuitacao },
  { value: 'Endosso', label: ptBR.casos.especimeEndosso },
  { value: 'Outro', label: ptBR.casos.especimeOutro },
] as const;

/** E74.F1 — opções do seletor de modalidade (ADR-0075 §1), na ordem de exibição. */
export const MODALIDADE_OPTIONS: readonly { value: Modalidade; label: string }[] = [
  { value: 'grafotecnica', label: ptBR.casos.modalidadeGrafotecnica },
  { value: 'eletronica', label: ptBR.casos.modalidadeEletronica },
];

export interface FormState {
  title: string;
  /** E74.F1 — escolhida na criação; no caso existente o campo fica travado. */
  modalidade: Modalidade;
  requester: string;
  processNumber: string;
  especime: string;
  parties: string;
  receivedAt: string;
  /** E163.F1 (ADR-0112 §4): a data do ato, opcional; vazia = não informada. */
  dataDoAto: string;
  deadline: string;
  cpf: string;
  contrato: string;
  /** E88.F1 (GUS-30 RF01/CA01) — o endereço do signatário. */
  endereco: string;
}

export interface FieldErrors {
  title?: string;
  processNumber?: string;
  deadline?: string;
  receivedAt?: string;
  cpf?: string;
}

export function todayIsoDate(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function validate(state: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (state.title.trim().length === 0) errors.title = ptBR.casos.errorMissingTitle;
  if (state.receivedAt.length === 0) errors.receivedAt = ptBR.casos.errorMissingReceivedAt;
  const proc = state.processNumber.trim();
  if (proc.length > 0 && proc !== CNJ_PLACEHOLDER && !CNJ_REGEX.test(proc)) {
    errors.processNumber = ptBR.casos.errorInvalidCnj;
  }
  if (state.deadline.length > 0 && state.deadline < state.receivedAt) {
    errors.deadline = ptBR.casos.errorDeadlineBeforeReceived;
  }
  // E126.F2 (GUS-105, ADR-0088 §2.4): o CPF deixou de ser obrigatório (a GUS-27 é
  // substituída): vem dos arquivos e a conferência pede só o que faltar. Informado,
  // o formato continua conferido.
  const cpf = state.cpf.trim();
  if (cpf.length > 0 && !CPF_REGEX.test(cpf)) {
    errors.cpf = ptBR.casos.errorInvalidCpf;
  }
  return errors;
}

/**
 * Hidrata o `FormState` a partir de um caso persistido (E33.F4 — passo
 * "Caso" editável do wizard). Colunas nulas viram string vazia para o input
 * controlado; o `buildPayload` reconverte vazio→null no save.
 */
export function formStateFromCase(record: CaseRecordData): FormState {
  const texto = (valor: string | null | undefined): string => valor ?? '';
  return {
    title: record.title,
    modalidade: record.modalidade ?? MODALIDADE_DEFAULT,
    requester: texto(record.requester),
    processNumber: texto(record.process_number),
    especime: texto(record.especime),
    parties: texto(record.parties),
    receivedAt: record.received_at,
    dataDoAto: texto(record.data_do_ato),
    deadline: texto(record.deadline),
    cpf: texto(record.cpf),
    contrato: texto(record.contrato),
    endereco: texto(record.endereco),
  };
}

export function buildPayload(state: FormState): CaseCreateData {
  const proc = state.processNumber.trim();
  const cpf = state.cpf.trim();
  const contrato = state.contrato.trim();
  const endereco = state.endereco.trim();
  return {
    title: state.title.trim(),
    modalidade: state.modalidade,
    requester: state.requester.trim().length > 0 ? state.requester.trim() : null,
    process_number: proc.length > 0 ? proc : null,
    especime: state.especime.length > 0 ? state.especime : null,
    parties: state.parties.trim().length > 0 ? state.parties.trim() : null,
    received_at: state.receivedAt,
    data_do_ato: state.dataDoAto.length > 0 ? state.dataDoAto : null,
    deadline: state.deadline.length > 0 ? state.deadline : null,
    cpf: cpf.length > 0 ? cpf : null,
    contrato: contrato.length > 0 ? contrato : null,
    endereco: endereco.length > 0 ? endereco : null,
  };
}

/**
 * Payload do PATCH (E33.F4): os mesmos campos da criação, menos a modalidade —
 * ela é fixada na criação e o servidor recusa o campo na atualização (E74.F1).
 *
 * F-E163.F1-01: a data do ato só vai quando muda. Reenviar a mesma data num
 * salvamento de outro campo atribuiria ao perito a data do cadastro anterior.
 */
export function buildUpdatePayload(state: FormState, original?: CaseRecordData): CaseUpdateData {
  const { modalidade: _modalidade, data_do_ato: ato, ...payload } = buildPayload(state);
  void _modalidade;
  const dataDoAto = ato ?? null;
  if (original !== undefined && dataDoAto === (original.data_do_ato ?? null)) return payload;
  return { ...payload, data_do_ato: dataDoAto };
}

export function mapErrorToMessage(err: unknown): string {
  if (err instanceof CaseError) {
    if (err.reason === 'validation') return ptBR.casos.errorValidation;
    if (err.reason === 'network') return ptBR.casos.errorNetwork;
  }
  return ptBR.casos.errorGeneric;
}
